<template>
  <v-dialog v-model="dialog" max-width="520px" :persistent="loading" @click:outside="close">
    <v-card>
      <v-card-title class="headline">
        Añadir compañía
        <v-spacer />
        <v-btn icon @click="close" :disabled="loading"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-form ref="form" v-model="formValid">
          <v-autocomplete
            v-model="form.companyId"
            :items="companies"
            item-text="name"
            item-value="id"
            label="Compañía"
            :rules="[v => !!v || 'Selecciona una compañía']"
            :loading="loadingCompanies"
            outlined
            dense
            @change="onCompanyChange"
          />
          <v-select
            v-model="form.contratoId"
            :items="contratos"
            item-text="label"
            item-value="id"
            label="Contrato"
            :rules="[v => !!v || 'Selecciona un contrato']"
            :loading="loadingContratos"
            :disabled="!form.companyId || !contratos.length"
            :hint="form.companyId && !contratos.length && !loadingContratos ? 'La compañía no tiene contratos' : ''"
            persistent-hint
            outlined
            dense
            @change="onContratoChange"
          />
          <v-select
            v-model="form.planId"
            :items="planOptions"
            label="Plan contratado"
            :loading="loadingPlanes"
            :disabled="!form.contratoId"
            hint="Con un plan, el usuario solo podrá activar ese plan del contrato"
            persistent-hint
            outlined
            dense
          />
          <v-text-field
            v-model.number="form.totalAccounts"
            type="number"
            min="1"
            label="N° de cuentas"
            :rules="[v => (v !== null && v !== '' && Number(v) >= 1) || 'Mínimo 1 cuenta']"
            outlined
            dense
          />
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">Cancelar</v-btn>
        <v-btn
          v-if="can('users:manage-company-access')"
          color="primary"
          :loading="loading"
          :disabled="!formValid"
          @click="submit"
        >
          Asociar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CompaniesService from '../services/companie.service.js';
import ContratoService from '../services/contrato.service.js';
import { can } from '../utils/permissions';

const EMPTY_FORM = { companyId: null, contratoId: null, planId: null, totalAccounts: 1, extraInfo: null };

export default {
  name: 'AssociateCompanyModal',
  props: {
    value: { type: Boolean, default: false },
    userId: { type: String, required: true }
  },
  data() {
    return {
      formValid: false,
      loading: false,
      loadingCompanies: false,
      loadingContratos: false,
      loadingPlanes: false,
      companies: [],
      contratos: [],
      planes: [],
      form: { ...EMPTY_FORM }
    };
  },
  computed: {
    dialog: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); }
    },
    // "Todos los planes" (null) deja el acceso sin restricción; un plan lo restringe a ese plan.
    planOptions() {
      return [
        { text: 'Todos los planes', value: null },
        ...this.planes.filter(p => p.visible).map(p => ({ text: p.plan.name, value: p.planId }))
      ];
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
        this.loadCompanies();
      }
    }
  },
  methods: {
    can,
    async loadCompanies() {
      if (this.companies.length) return;
      this.loadingCompanies = true;
      const res = await CompaniesService.getCompaniesWithCountry();
      this.companies = res.status === 'ok' && Array.isArray(res.data) ? res.data : [];
      this.loadingCompanies = false;
    },
    async onCompanyChange(companyId) {
      this.form.contratoId = null;
      this.form.planId = null;
      this.contratos = [];
      this.planes = [];
      if (!companyId) return;
      this.loadingContratos = true;
      const res = await ContratoService.getByCompanyId(companyId);
      const list = res.status === 'ok' && Array.isArray(res.data) ? res.data : [];
      this.contratos = list.map(c => ({
        id: c.id,
        label: this.contratoLabel(c)
      }));
      // Auto-select when the company has a single contrato.
      if (this.contratos.length === 1) {
        this.form.contratoId = this.contratos[0].id;
        await this.onContratoChange(this.form.contratoId);
      }
      this.loadingContratos = false;
    },
    async onContratoChange(contratoId) {
      this.form.planId = null;
      this.planes = [];
      if (!contratoId) return;
      this.loadingPlanes = true;
      const res = await ContratoService.getById(contratoId);
      this.planes = res.status === 'ok' && Array.isArray(res.data?.planes) ? res.data.planes : [];
      this.loadingPlanes = false;
    },
    contratoLabel(c) {
      const planName = c.plan && c.plan.name ? ` · ${c.plan.name}` : '';
      const status = c.status ? ` (${c.status})` : '';
      return `${c.codigo || 'Contrato'}${planName}${status}`;
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      const res = await CompaniesService.associateUserCompany(this.userId, {
        companyId: this.form.companyId,
        contratoId: this.form.contratoId,
        planId: this.form.planId,
        totalAccounts: Number(this.form.totalAccounts),
        extraInfo: this.form.extraInfo || null
      });
      this.loading = false;
      if (res.status === 'ok') {
        this.$emit('saved');
        this.close();
      } else {
        this.$emit('error', this.mapError(res.message));
      }
    },
    mapError(code) {
      const map = {
        ASSOCIATION_ALREADY_EXISTS: 'El usuario ya está asociado a ese contrato.',
        CONTRATO_DOES_NOT_BELONG_TO_COMPANY: 'El contrato seleccionado no pertenece a la compañía.',
        CONTRATO_NOT_FOUND: 'El contrato no existe.',
        PLAN_NOT_IN_CONTRATO: 'El plan elegido no pertenece al contrato.',
        TOTAL_ACCOUNTS_MUST_BE_POSITIVE: 'El número de cuentas debe ser al menos 1.'
      };
      return map[code] || 'No se pudo asociar la compañía.';
    },
    resetForm() {
      this.form = { ...EMPTY_FORM };
      this.contratos = [];
      this.planes = [];
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.dialog = false;
    }
  }
};
</script>
